// PaywallGate — v0.8.0
//
// Wraps a protected page. When the user's trial is expired and they are
// not a paying subscriber, renders an upgrade prompt instead of the page.
// While subscription state is still loading, renders nothing (avoids flash).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { track } from '../lib/analytics';
import { isBillingEnabled, recordUpgradeIntent } from '../lib/upgradeIntent';

export default function PaywallGate({ children }) {
  const { loading, isExpired, isPro, isTrialing, tier, status, startCheckout } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError]     = useState(null);
  // BL-038 Story 38.5 — when billing is OFF (lockdown), the upgrade CTA captures
  // intent instead of opening the inert Stripe checkout; this flags the ack.
  const [intentRecorded, setIntentRecorded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isPro && !isTrialing && !loading) {
      track('paywall_hit', { tier, status });
    }
  }, [tier, status]);

  // Still loading — render nothing to avoid a flash.
  if (loading) return null;

  // Active subscriber or still in trial — show the page normally.
  if (isPro || isTrialing) return <>{children}</>;

  // Trial expired and not subscribed — show the paywall.
  async function handleUpgrade() {
    // BL-038 Story 38.5 — billing OFF (lockdown): never dead-end into the inert
    // Stripe checkout. Capture upgrade intent and acknowledge it instead.
    if (!isBillingEnabled()) {
      recordUpgradeIntent({ from: 'paywall_gate', currentTier: tier });
      setIntentRecorded(true);
      return;
    }
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      await startCheckout();
      // startCheckout redirects on success, so we only reach here on error.
    } catch (err) {
      setCheckoutError(err.message || 'Something went wrong. Please try again.');
      setCheckoutLoading(false);
    }
  }

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={iconStyle}>🔒</div>
        <h2 style={headingStyle}>Your free trial has ended</h2>
        {intentRecorded ? (
          <p style={bodyStyle}>
            Thanks — we&apos;ll be in touch about upgrading your access. In the
            meantime you can review the plans below.
          </p>
        ) : (
          <p style={bodyStyle}>
            Upgrade to Investor to continue running analyses, saving deals,
            and accessing the full investor workspace.
          </p>
        )}

        {checkoutError && (
          <p style={errorStyle}>{checkoutError}</p>
        )}

        {!intentRecorded && (
          <button
            style={upgradeButtonStyle}
            onClick={handleUpgrade}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? 'Redirecting to checkout…' : 'Upgrade to Investor →'}
          </button>
        )}

        <button
          style={pricingLinkStyle}
          onClick={() => navigate('/pricing')}
        >
          View pricing plans
        </button>
      </div>
    </div>
  );
}

const overlayStyle = {
  minHeight: '100vh',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '20px',
  padding: '48px 40px',
  maxWidth: '480px',
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
};

const iconStyle = {
  fontSize: '48px',
  marginBottom: '16px',
};

const headingStyle = {
  fontSize: '24px',
  fontWeight: 800,
  color: '#111827',
  margin: '0 0 12px',
};

const bodyStyle = {
  fontSize: '15px',
  color: '#6b7280',
  lineHeight: 1.6,
  margin: '0 0 28px',
};

const upgradeButtonStyle = {
  display: 'block',
  width: '100%',
  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  color: '#ffffff',
  border: 'none',
  borderRadius: '12px',
  padding: '14px 24px',
  fontSize: '16px',
  fontWeight: 700,
  cursor: 'pointer',
  marginBottom: '12px',
};

const pricingLinkStyle = {
  background: 'transparent',
  border: 'none',
  color: '#2563eb',
  fontSize: '14px',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: '4px',
};

const errorStyle = {
  color: '#ef4444',
  fontSize: '13px',
  marginBottom: '12px',
};
